import { datePl, type FleetDashboardDto, type MeDto, type ObligationDto, TYRE_NEEDS, TYRE_SEASONS, UNAVAILABILITY_REASONS } from '@bqcrm/shared';
import { Anchor, Badge, Button, Card, Group, SimpleGrid, Stack, Table, Text } from '@mantine/core';
import { IconCheck, IconCircleCheck, IconX } from '@tabler/icons-react';
import { useMutation, useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { api } from '../../api';
import { can } from '../../auth';
import { BlocksUseBadge, FleetReportStatusBadge, ObligationStateBadge } from '../../components/Badges';
import { PageHeader } from '../../components/PageHeader';
import { QueryState } from '../../components/QueryState';
import { SectionCard } from '../../components/ui';
import { dayRange, formatDateTime } from '../../format';
import { notifyError, notifyOk } from '../../notify';
import { FleetShell, kmPl, useFleetInvalidate } from './common';

const vehicleLink = (id: string, tab?: string, extra = '') => `/flota/pojazdy/${id}${tab ? `?zakladka=${tab}${extra}` : ''}`;

function ObligationRows({ rows }: { rows: ObligationDto[] }) {
  return (
    <Table.ScrollContainer minWidth={560}>
      <Table highlightOnHover>
        <Table.Tbody>
          {rows.map((o) => (
            <Table.Tr key={o.id}>
              <Table.Td w={130}>
                <Anchor component={Link} to={vehicleLink(o.vehicleId, 'terminy', `&czynnosc=${o.id}`)} size="sm" fw={600}>
                  {o.plate}
                </Anchor>
              </Table.Td>
              <Table.Td>
                <Text size="sm">{o.typeName}</Text>
                <Text size="xs" c="dimmed">
                  {[o.dueDate ? `termin ${datePl(o.dueDate)}` : null, o.dueKm !== null ? `limit ${kmPl(o.dueKm)}` : null, o.eval.reason].filter(Boolean).join(' · ')}
                </Text>
              </Table.Td>
              <Table.Td ta="right">
                <Group gap={4} justify="flex-end" wrap="nowrap">
                  {o.visit && (
                    <Badge size="sm" variant="light" color="teal">
                      wizyta {datePl(o.visit.dateFrom)}
                    </Badge>
                  )}
                  <ObligationStateBadge state={o.eval.state} reason={o.eval.reason} />
                </Group>
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Table.ScrollContainer>
  );
}

function Rows({ children, minWidth = 480 }: { children: ReactNode; minWidth?: number }) {
  return (
    <Table.ScrollContainer minWidth={minWidth}>
      <Table highlightOnHover>
        <Table.Tbody>{children}</Table.Tbody>
      </Table>
    </Table.ScrollContainer>
  );
}

interface Section {
  key: string;
  title: string;
  count: number;
  tone?: 'red' | 'orange';
  body: ReactNode;
}

/** Panel floty — lista działań zarządzającego: każdy wiersz prowadzi do pojazdu, zgłoszenia albo importu. */
export function FleetDashboardPage({ me }: { me: MeDto }) {
  const q = useQuery({ queryKey: ['fleet', 'dashboard'], queryFn: () => api<FleetDashboardDto>('/fleet/dashboard'), refetchInterval: 60_000, refetchOnWindowFocus: true });
  const invalidate = useFleetInvalidate();
  const manage = can(me, 'flota.zarzadzanie');
  const verify = useMutation({
    mutationFn: (v: { id: string; decision: 'ok' | 'odrzucony' }) => api(`/fleet/odometer/${v.id}/verify`, { method: 'POST', body: { decision: v.decision, note: '' } }),
    onSuccess: (_r, v) => {
      notifyOk(v.decision === 'ok' ? 'Potwierdzono odczyt' : 'Odrzucono odczyt');
      invalidate();
    },
    onError: notifyError,
  });
  const d = q.data;
  const urgent = d?.openReports.filter((r) => r.blocksUse) ?? [];
  const reports = d ? [...urgent, ...d.openReports.filter((r) => !r.blocksUse)] : [];

  const sections: Section[] = d
    ? [
        { key: 'overdue', title: 'Przekroczone terminy', count: d.overdue.length, tone: 'red', body: <ObligationRows rows={d.overdue} /> },
        {
          key: 'reports',
          title: urgent.length ? `Otwarte zgłoszenia (pilne: ${urgent.length})` : 'Otwarte zgłoszenia',
          count: reports.length,
          tone: urgent.length ? 'red' : undefined,
          body: (
            <Rows>
              {reports.map((r) => (
                <Table.Tr key={r.id} style={r.blocksUse ? { backgroundColor: 'var(--mantine-color-red-light)' } : undefined}>
                  <Table.Td w={130}>
                    <Anchor component={Link} to={`/flota/zgloszenia?zgloszenie=${r.id}`} size="sm" fw={600}>
                      {r.number}
                    </Anchor>
                    <Text size="xs" c="dimmed">
                      {r.plate}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" lineClamp={2}>
                      {r.description}
                    </Text>
                    <Text size="xs" c="dimmed">
                      {r.reporter.name} · {formatDateTime(r.createdAt)}
                    </Text>
                  </Table.Td>
                  <Table.Td ta="right">
                    <Group gap={4} justify="flex-end" wrap="nowrap">
                      {r.blocksUse && <BlocksUseBadge />}
                      <FleetReportStatusBadge status={r.status} />
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Rows>
          ),
        },
        { key: 'toSchedule', title: 'Serwisy do umówienia', count: d.toSchedule.length, tone: 'orange', body: <ObligationRows rows={d.toSchedule} /> },
        { key: 'upcoming', title: 'Zbliżające się terminy', count: d.upcoming.length, body: <ObligationRows rows={d.upcoming} /> },
        { key: 'nearKm', title: 'Blisko limitu przebiegu', count: d.nearKm.length, body: <ObligationRows rows={d.nearKm} /> },
        {
          key: 'verify',
          title: 'Odczyty przebiegu do weryfikacji',
          count: d.odometerToVerify.length,
          tone: 'orange',
          body: (
            <Rows minWidth={600}>
              {d.odometerToVerify.map((r) => (
                <Table.Tr key={r.readingId}>
                  <Table.Td w={130}>
                    <Anchor component={Link} to={vehicleLink(r.vehicleId, 'przebieg')} size="sm" fw={600}>
                      {r.plate}
                    </Anchor>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">
                      {kmPl(r.km)} · odczyt {formatDateTime(r.readAt)}
                    </Text>
                    <Text size="xs" c="var(--app-text-orange)">
                      {r.issue}
                    </Text>
                  </Table.Td>
                  <Table.Td ta="right">
                    {manage && (
                      <Group gap={4} justify="flex-end" wrap="nowrap">
                        <Button size="compact-sm" variant="light" color="green" leftSection={<IconCheck size={14} />} loading={verify.isPending && verify.variables?.id === r.readingId} onClick={() => verify.mutate({ id: r.readingId, decision: 'ok' })}>
                          Potwierdź
                        </Button>
                        <Button size="compact-sm" variant="subtle" color="red" leftSection={<IconX size={14} />} onClick={() => verify.mutate({ id: r.readingId, decision: 'odrzucony' })}>
                          Odrzuć
                        </Button>
                      </Group>
                    )}
                  </Table.Td>
                </Table.Tr>
              ))}
            </Rows>
          ),
        },
        {
          key: 'stale',
          title: 'Nieaktualne odczyty przebiegu',
          count: d.staleOdometer.length,
          body: (
            <Rows>
              {d.staleOdometer.map((r) => (
                <Table.Tr key={r.vehicleId}>
                  <Table.Td w={130}>
                    <Anchor component={Link} to={vehicleLink(r.vehicleId, 'przebieg')} size="sm" fw={600}>
                      {r.plate}
                    </Anchor>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">{r.readAt ? `ostatni odczyt ${kmPl(r.km)} z ${datePl(r.readAt)}` : 'brak odczytu przebiegu'}</Text>
                    <Text size="xs" c="dimmed">
                      czynności kilometrowe bez aktualnych danych: {r.obligations}
                    </Text>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Rows>
          ),
        },
        {
          key: 'unavailable',
          title: 'Pojazdy niedostępne',
          count: d.unavailable.length,
          body: (
            <Rows>
              {d.unavailable.map((u) => (
                <Table.Tr key={u.id}>
                  <Table.Td w={130}>
                    <Anchor component={Link} to={vehicleLink(u.vehicleId, 'dostepnosc')} size="sm" fw={600}>
                      {u.plate}
                    </Anchor>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">
                      {UNAVAILABILITY_REASONS[u.reason]} · {dayRange(u.dateFrom, u.dateTo)}
                    </Text>
                    {(u.note || u.replacement) && (
                      <Text size="xs" c="dimmed">
                        {[u.note, u.replacement ? `auto zastępcze: ${u.replacement.plate}` : null].filter(Boolean).join(' · ')}
                      </Text>
                    )}
                  </Table.Td>
                </Table.Tr>
              ))}
            </Rows>
          ),
        },
        {
          key: 'conflicts',
          title: 'Konflikty z planem prac',
          count: d.conflicts.length,
          tone: 'orange',
          body: (
            <Rows>
              {d.conflicts.map((c, i) => (
                <Table.Tr key={`${c.unavailabilityId}-${c.taskId}-${c.day}-${i}`}>
                  <Table.Td w={130}>
                    <Anchor component={Link} to={vehicleLink(c.vehicleId, 'dostepnosc')} size="sm" fw={600}>
                      {c.plate}
                    </Anchor>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">{c.text}</Text>
                    <Text size="xs" c="dimmed">
                      <Anchor component={Link} to={`/planowanie?dzien=${c.day}`} size="xs">
                        {datePl(c.day)} · {c.taskNumber} {c.taskTitle}
                      </Anchor>
                      {c.teamName ? ` · ${c.teamName}` : ''}
                      {c.replacementPlate ? ` · auto zastępcze: ${c.replacementPlate}` : ''}
                    </Text>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Rows>
          ),
        },
        {
          key: 'noResponsible',
          title: 'Bez odpowiedzialnego / zmiana lidera do potwierdzenia',
          count: d.noResponsible.length,
          tone: 'orange',
          body: (
            <Rows>
              {d.noResponsible.map((r) => (
                <Table.Tr key={r.vehicleId}>
                  <Table.Td w={130}>
                    <Anchor component={Link} to={vehicleLink(r.vehicleId, 'przypisanie')} size="sm" fw={600}>
                      {r.plate}
                    </Anchor>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">{r.reason}</Text>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Rows>
          ),
        },
        {
          key: 'tyres',
          title: 'Potrzeby opon',
          count: d.tyreNeeds.length,
          body: (
            <Rows>
              {d.tyreNeeds.map((t) => (
                <Table.Tr key={t.setId}>
                  <Table.Td w={130}>
                    <Anchor component={Link} to={vehicleLink(t.vehicleId, 'opony')} size="sm" fw={600}>
                      {t.plate}
                    </Anchor>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">
                      {TYRE_SEASONS[t.season]} — potrzeba: {TYRE_NEEDS[t.need].toLowerCase()}
                    </Text>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Rows>
          ),
        },
        {
          key: 'import',
          title: 'Transakcje paliwowe do wyjaśnienia',
          count: d.importIssues.length,
          body: (
            <Rows>
              {d.importIssues.map((i) => (
                <Table.Tr key={i.batchId}>
                  <Table.Td>
                    <Anchor component={Link} to="/flota/import" size="sm" fw={600}>
                      {i.companyName}: {i.pending} do wyjaśnienia
                    </Anchor>
                    <Text size="xs" c="dimmed">
                      import z {formatDateTime(i.createdAt)}
                    </Text>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Rows>
          ),
        },
      ]
    : [];
  const full = sections.filter((s) => s.count > 0);
  const empty = sections.filter((s) => s.count === 0);

  return (
    <FleetShell me={me}>
      <PageHeader title="Flota" description="Działania do wykonania: terminy, serwisy do umówienia, zgłoszenia, odczyty przebiegu, niedostępne pojazdy i konflikty z planem prac." />
      <QueryState isLoading={q.isLoading} error={q.error}>
        <Stack gap="md">
          {d && !full.length && (
            <Card>
              <Group gap="sm">
                <IconCircleCheck size={22} color="var(--mantine-color-green-6)" />
                <Text fw={600}>Nic do zrobienia — wszystkie terminy w porządku, brak otwartych zgłoszeń.</Text>
              </Group>
            </Card>
          )}
          <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="md">
            {full.map((s) => (
              <SectionCard
                key={s.key}
                title={
                  <Group gap={6} wrap="nowrap">
                    <Text fw={700} size="sm" c={s.tone === 'red' ? 'var(--app-text-red)' : undefined}>
                      {s.title}
                    </Text>
                    <Badge size="sm" variant={s.tone ? 'filled' : 'light'} color={s.tone ?? 'gray'} circle={s.count < 10}>
                      {s.count}
                    </Badge>
                  </Group>
                }
                padding={0}
              >
                {s.body}
              </SectionCard>
            ))}
          </SimpleGrid>
          {d && empty.length > 0 && full.length > 0 && (
            <Text size="sm" c="dimmed">
              Nic do zrobienia: {empty.map((s) => s.title.toLowerCase()).join(', ')}.
            </Text>
          )}
        </Stack>
      </QueryState>
    </FleetShell>
  );
}
